import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useI18n } from "@/lib/i18n";
import { pixoAsset } from "@/lib/pixo";
import UpDownMark from "@/components/UpDownMark";
import "@/components/pixo/pixo.css";

/** Opens when a logged-out visitor tries to vote: what a free account gets them (batch 17). */
export function askToSignUp() {
  window.dispatchEvent(new CustomEvent("tier:signup"));
}

// order follows S.perks: vote, levels, Pixie, daily chest, mini games
const PERK_ICONS = [
  () => pixoAsset.ach("first_vote"),
  () => pixoAsset.ach("early_supporter"),
  () => pixoAsset.body("happy"),
  () => pixoAsset.chest,
  () => pixoAsset.fish("holo_koi"),
];

export default function SignupPrompt() {
  const { t } = useI18n();
  const S = t.eco.signup;
  const [open, setOpen] = useState(false);
  const [frame, setFrame] = useState(1);
  const navigate = useNavigate();
  const location = useLocation();
  const timer = useRef(null);

  useEffect(() => {
    const on = () => setOpen(true);
    window.addEventListener("tier:signup", on);
    return () => window.removeEventListener("tier:signup", on);
  }, []);
  useEffect(() => {
    clearInterval(timer.current);
    if (open) timer.current = setInterval(() => setFrame((f) => (f === 1 ? 2 : 1)), 320);
    return () => clearInterval(timer.current);
  }, [open]);

  const go = (path) => {
    setOpen(false);
    navigate(path, { state: { next: location.pathname + location.search } });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent onOpenAutoFocus={(e) => e.preventDefault()} data-testid="signup-prompt" className="max-w-md bg-[#16161b] border border-amber-500/25 rounded-sm sm:rounded-sm text-stone-100 p-6 max-h-[92vh] overflow-y-auto">
        <div className="grid grid-cols-[auto_1fr] gap-2 sm:gap-3 items-center pr-6">
          {/* the landing-page Pixie in tierTCG colours (beret + gold mark), waving */}
          <div className="relative w-[140px] h-[100px] sm:w-[196px] sm:h-[140px] -my-3 -ml-2 -mr-7 sm:-mr-10" aria-hidden>
            <img src={`/pixo/hero/body-wave${frame}.png`} alt="" className="absolute inset-0 w-full h-full" style={{ imageRendering: "pixelated" }} />
            <img src="/pixo/hero/beret.png" alt="" className="absolute inset-0 w-full h-full" style={{ imageRendering: "pixelated" }} />
          </div>
          {/* title centred in the space right of Pixie */}
          <DialogTitle className="flex flex-col items-start text-left font-display text-[28px] sm:text-[36px] font-black uppercase leading-[0.95]">
            <span className="whitespace-nowrap">{S.title}</span>
            {/* tierTCG up/down mark sits inside the word, cap-height, so it reads as "vote" */}
            <span className="signup-hl text-amber-400">
              <span className="flex items-center gap-[0.14em] whitespace-nowrap">
                <UpDownMark className="h-[0.74em] w-[0.45em] shrink-0 -translate-y-[0.04em]" />
                {S.title_hl}
              </span>
            </span>
          </DialogTitle>
        </div>
        <DialogDescription className="text-sm text-stone-400">{S.lede}</DialogDescription>
        <ul className="grid gap-2.5">
          {S.perks.map(([title, sub], i) => (
            <li key={i} className="grid grid-cols-[34px_1fr] gap-2.5 items-center">
              <span className="w-[34px] h-[34px] grid place-items-center border border-amber-500/25 bg-[#111114]">
                <img src={PERK_ICONS[i]()} alt="" style={{ maxWidth: 26, maxHeight: 26, imageRendering: "pixelated" }} />
              </span>
              <span className="text-sm text-stone-100 leading-tight">
                {title}
                <span className="block text-xs text-stone-500">{sub}</span>
              </span>
            </li>
          ))}
        </ul>
        <div className="grid sm:grid-cols-2 gap-2 mt-1">
          <button type="button" onClick={() => go("/register")} data-testid="signup-create" className="btn-gold px-4 py-3 text-[11px] font-bold uppercase tracking-[0.18em] rounded-sm">
            {S.create}
          </button>
          <button type="button" onClick={() => go("/login")} data-testid="signup-login" className="border border-amber-500/45 text-amber-200 hover:border-amber-300 px-4 py-3 text-[11px] font-bold uppercase tracking-[0.18em] rounded-sm">
            {S.login}
          </button>
        </div>
        <p className="text-[11px] text-stone-500 text-center">{S.foot}</p>
      </DialogContent>
    </Dialog>
  );
}
